import { useState, useMemo, useEffect } from "react";
import ambassadorBg from "assets/images/others/ambassador-side-images/ambassadorBg.png";
import { ReactComponent as Medal } from "assets/images/svgs/ambassador/medal.svg";
import { ReactComponent as Award } from "assets/images/svgs/ambassador/award.svg";
import { ReactComponent as CallenderSvg } from "assets/images/svgs/ambassador/callenderSvg.svg";
import { ReactComponent as EditIcon } from "assets/images/svgs/ambassador/edit.svg";
import { ReactComponent as DateICon } from "assets/images/svgs/ambassador/dateSvg.svg";
import { ReactComponent as InforCircle } from "assets/images/svgs/info-circle.svg";
import { 
  MdCalendarToday as CalendarTick,
  MdEvent as Calendar,
  MdClose as CrossRed,
  MdSupportAgent as SupportAgent,
  MdArrowForward as ArrowForward
} from "react-icons/md";
import LearnLocalLogo from "assets/images/others/host-side-images/LL-Full Color-4.png";
import editProfileUser from "assets/images/others/host-side-images/editProfileUSer.png";
import ambassadorMainImg from "assets/images/others/ambassador-side-images/ambassadorMainImg.png";
import DrawerAmbassadorRole from "./component/ambassador-role";
import useStore from "hooks/useStore";
import { siteRoutes } from "utils/helpers/enums/routes";
import { useNavigate } from "react-router-dom";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import useUtils from "hooks/useUtils";
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  format,
  isAfter,
  isBefore,
} from "date-fns";
import AmbassadorFamilyprofile from "../family-profile";
import { AMBASSADOR_EVENT_STATUS } from "utils/helpers/enums/shared.enum";
import { useSelector } from "react-redux";
import useAuth from "containers/auth/useHooks";
import ProfileCompletionModal from "./component/ProfileCompletionModal";

// TypeScript interfaces for better type safety
interface AmbassadorState {
  isProfileCompleted: boolean;
  bookedSessions: any;
  eventStatus: string;
  isApproved: boolean | null;
  sessionStartTime: Date | null;
  sessionEndTime: Date | null;
  currentTime: Date;
}

// Profile level enum matching Flutter code
enum ProfileLevel {
  approved = 'approved',
  sessionOnGoing = 'sessionOnGoing',
  waitingForApproval = 'waitingForApproval',
  profileCompleted = 'profileCompleted',
  rejected = 'rejected',
  profileUnCompleted = 'profileUnCompleted',
}

const AmbassadorHome = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { getDateTime } = useUtils();
  const { getUserProfile } = useAuth();
  const {
    setProfileModalOpen,
  } = useStore();

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );
  const profileModalOpen = useSelector(
    (state: any) => state.sharedReducer.profileModalOpen
  );

  // Memoized ambassador state calculation
  const ambassadorState: AmbassadorState = useMemo(() => {
    const ambassador = registeredData?.profiles?.ambassador;
    const bookedSessions = ambassador?.bookedSessions;
    const lastAdminEvent = ambassador?.lastAdminEvent;
    const currentTime = new Date();
    
    return {
      isProfileCompleted: ambassador?.profileCompleted || false,
      bookedSessions,
      eventStatus: ambassador?.eventStatus || "",
      isApproved: ambassador?.isApproved,
      sessionStartTime: bookedSessions?.startTime ? new Date(bookedSessions.startTime) : null,
      sessionEndTime: bookedSessions?.endTime ? new Date(bookedSessions.endTime) : null,
      currentTime,
    };
  }, [registeredData]);

  // Get data directly from registeredData API response
  const userProfilePic = registeredData?.user?.profilePicUrl?.fileDownloadUri 
    ? concatPathWithBackendUrl(registeredData.user.profilePicUrl.fileDownloadUri)
    : editProfileUser;
  const userFullName = `${registeredData?.user?.firstName || ''} ${registeredData?.user?.lastName || ''}`.trim();
  const missionStatement = registeredData?.profiles?.ambassador?.mission || '';
  const ambassadorprofileType = registeredData?.profiles?.ambassador?.profileType;
  const pastAmbassadorCreatedAt = registeredData?.profiles?.ambassador?.createdAt;
  const ambassadorTier = registeredData?.profiles?.ambassador?.tier;
  const lastAdminEvent = registeredData?.profiles?.ambassador?.lastAdminEvent;

  useEffect(() => {
    getUserProfile();
  }, []);

  // Profile completion modal logic - matching Flutter app behavior
  useEffect(() => {
    const profileLevel = checkProfileLevel();
    
    // Don't show modal if approved (matching Flutter: if approved, just navigate)
    if (profileLevel === ProfileLevel.approved) {
      setProfileModalOpen(false);
      return;
    }
    
    // Show modal for all other profile levels (matching Flutter: checkProfileLevelWithAlerts)
    // But only if we're not already on the profile page
    if (window.location.pathname !== siteRoutes.ambassadorProfile) {
      setProfileModalOpen(true);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredData, ambassadorState.isProfileCompleted, ambassadorState.isApproved]);

  // Calculate time since ambassador creation
  const timeSince = useMemo(() => {
    if (!pastAmbassadorCreatedAt) return "";
    
    const now = new Date();
    const createdAt = new Date(pastAmbassadorCreatedAt);
    const years = differenceInYears(now, createdAt);
    const months = differenceInMonths(now, createdAt);
    const days = differenceInDays(now, createdAt);
    
    if (years >= 1) {
      return `${years} year${years > 1 ? "s" : ""}`;
    } else if (months >= 1) {
      return `${months} month${months > 1 ? "s" : ""}`;
    } else {
      return `${days} day${days > 1 ? "s" : ""}`;
    }
  }, [pastAmbassadorCreatedAt]);

  // Format session time
  const formattedTime = useMemo(() => {
    const bookedSessions = ambassadorState.bookedSessions;
    if (!bookedSessions?.startTime || !bookedSessions?.endTime) return "";
    
    try {
      const startTime = new Date(bookedSessions.startTime);
      const endTime = new Date(bookedSessions.endTime);
      
      return `${format(startTime, "EEEE")}, ${format(startTime, "hh:mm a")} - ${format(endTime, "hh:mm a")}`;
    } catch (e) {
      console.error("Date formatting failed:", e);
      return "";
    }
  }, [ambassadorState.bookedSessions]);

  // Event handlers
  const handleJoinClick = () => {
    if (ambassadorState.bookedSessions?.meetingLink) {
      window.open(ambassadorState.bookedSessions?.meetingLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleContactSupport = () => {
    // Navigate to support or open email
    window.location.href = "mailto:info@learnlocalapp.com";
  };

  const handleBrowseEvents = () => {
    // Navigate to events list
    navigate(siteRoutes.ambassadorExperiences);
  };

    const goToAmbassadorAdminExperienceDetailView = (id: number) => {
    navigate(`${siteRoutes.ambassadorAdminExperienceDetail}?id=${id}`);
  };


  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Check profile level - matching Flutter logic exactly
  const checkProfileLevel = (): ProfileLevel => {
    const ambassador = registeredData?.profiles?.ambassador;

    if (ambassador == null || !ambassador.profileCompleted) {
      return ProfileLevel.profileUnCompleted;
    }

    if (ambassador.isApproved == null) {
      if (ambassador.bookedSessions != null && 
          ambassador.bookedSessions.startTime) {
        const sessionStartTime = new Date(ambassador.bookedSessions.startTime);
        if (isAfter(sessionStartTime, new Date())) {
          return ProfileLevel.sessionOnGoing;
        }
      }
      return ProfileLevel.waitingForApproval;
    }

    return ambassador.isApproved
      ? ProfileLevel.approved
      : ProfileLevel.rejected;
  };

  // Determine user level (0, 1, or 3) - matching Flutter logic
  const determineUserLevel = (): number => {
    const user = registeredData?.user;
    const ambassadorProfile = registeredData?.profiles?.ambassador;

    // Check if user profile is complete
    const isUserComplete =
      user?.firstName &&
      user?.lastName &&
      user?.email &&
      user?.language &&
      user?.address &&
      user?.phone &&
      user?.dob &&
      user?.gender &&
      user?.state &&
      user?.city &&
      user?.zipCode;

    // Check if ambassador profile is complete
    // Skills can be string or array - check both cases
    const hasSkills = ambassadorProfile?.skills != null && 
      (Array.isArray(ambassadorProfile.skills) 
        ? ambassadorProfile.skills.length > 0 
        : String(ambassadorProfile.skills).trim() !== '');
    
    const isAmbassadorComplete =
      ambassadorProfile != null &&
      hasSkills &&
      ambassadorProfile.mission &&
      ambassadorProfile.engagementLevel &&
      ambassadorProfile.commitmentLength;

    if (isAmbassadorComplete) {
      return 3;
    } else if (isUserComplete) {
      return 1;
    } else {
      return 0;
    }
  };

  const getAmbassadorUIState = (): 'profile-incomplete' | 'session-ui' | 'event-passed-waiting' | 'waiting-for-approval' | 'not-attended' | 'rejected' | 'family-profile' | 'default' => {
    const { isProfileCompleted, bookedSessions, eventStatus, sessionStartTime, currentTime } = ambassadorState;

    // Check for not-attended case first (specific event status)
    if (isProfileCompleted && 
        sessionStartTime && 
        isBefore(sessionStartTime, currentTime) && 
        eventStatus === AMBASSADOR_EVENT_STATUS.NOT_ATTENDED) {
      return 'not-attended';
    }

    // Use the checkProfileLevel function matching Flutter logic
    const profileLevel = checkProfileLevel();

    switch (profileLevel) {
      case ProfileLevel.profileUnCompleted:
        return 'profile-incomplete';

      case ProfileLevel.sessionOnGoing:
        return 'session-ui';

      case ProfileLevel.waitingForApproval:
        // Check if there's a past session waiting for attendance marking
        if (bookedSessions && 
            sessionStartTime && 
            isBefore(sessionStartTime, currentTime) && 
            eventStatus === AMBASSADOR_EVENT_STATUS.SUBSCRIBED) {
          return 'event-passed-waiting';
        }
        return 'waiting-for-approval';

      case ProfileLevel.approved:
        // Approved ambassador - show default UI
        // return 'default';
        return 'family-profile';


      case ProfileLevel.rejected:
        // When rejected (isApproved === false), show family profile
        // This matches the original web app behavior
        return 'default';

      default:
        return 'default';
    }
  };

  const currentUIState = getAmbassadorUIState();
  const userLevel = determineUserLevel(); // Can be used for display or logic if needed

  // UI Components for different states
  const renderEventPassedWaitingUI = () => (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-full sm:w-[440px] text-center">
        <div className="flex flex-col items-center justify-center gap-2">
          <CalendarTick className="w-16 h-16 text-ambassadorBlue" />
          <h2 className="text-ambassadorDarkBlue text-xl font-semibold">
            Waiting for Attendance Confirmation
          </h2>
          <p className="text-center text-blackie text-sm leading-relaxed">
            The event has ended. Please wait while the admin marks your attendance. Once confirmed, the next steps will begin.
          </p>
          <button
            onClick={handleContactSupport}
            className="mt-4 px-6 py-3 bg-ambassadorBlue text-white rounded-lg flex items-center gap-2"
          >
            <SupportAgent className="w-5 h-5" />
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );

  const renderNotAttendedUI = () => (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-full sm:w-[440px] text-center">
        <div className="flex flex-col items-center justify-center gap-2">
          <Calendar className="w-16 h-16 text-ambassadorBlue" />
          <h2 className="text-ambassadorDarkBlue text-xl font-semibold">
            You Did Not Attend the Event
          </h2>
          <p className="text-center text-blackie text-sm leading-relaxed">
            It looks like you missed the scheduled event. If you missed it for any reason, don't worry! You can join other available events.
          </p>
          <button
            onClick={handleBrowseEvents}
            className="mt-4 px-6 py-3 bg-ambassadorBlue text-white rounded-lg flex items-center gap-2"
          >
            Browse Events
            <ArrowForward className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderRejectedUI = () => (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-full sm:w-[440px] text-center">
        <div className="flex flex-col items-center justify-center gap-2">
          <CrossRed className="w-16 h-16 text-red-500" />
          <h2 className="text-ambassadorDarkBlue text-xl font-semibold">
            Ambassador Dismissal
          </h2>
          <p className="text-center text-blackie text-sm leading-relaxed">
            Your account has been suspended for further questions please contact{" "}
            <button
              onClick={handleContactSupport}
              className="text-ambassadorBlue font-bold underline "
            >
              info@learnlocalapp.com
            </button>
            .
          </p>
        </div>
      </div>
    </div>
  );

  const renderWaitingForApprovalUI = () => (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-full sm:w-[440px] text-center">
        <div className="flex flex-col items-center justify-center gap-2">
          <CalendarTick className="w-16 h-16 text-ambassadorBlue" />
          <h2 className="text-ambassadorDarkBlue text-xl font-semibold">
            Waiting for Approval
          </h2>
          <p className="text-center text-blackie text-sm leading-relaxed">
            Your attendance has been confirmed. Please wait while the admin reviews your application for approval.
          </p>
        </div>
      </div>
    </div>
  );

  const renderSessionUI = () => (
    <div className="min-h-[50vh] flex flex-col gap-4">
      <div className="flex flex-col gap-2 w-[351px]">
        <h1 className="flex justify-between items-center text-blackie text-lg font-medium">
          Mission Statement
          <span
            className="cursor-pointer"
            onClick={() => navigate(siteRoutes.ambassadorEditProfile)}
          >
            <EditIcon />
          </span>
        </h1>
        <p className="text-sm text-blackie">{missionStatement}</p>
      </div>
      <div className="flex flex-col gap-2 w-[351px]">
        <h1 className="flex justify-between items-center text-blackie text-lg font-semibold">
          Interested in being a community leader?
        </h1>
        <p className="text-sm text-primaryBlueText">
          Take a Live Session Now!
        </p>
      </div>
      <div className="w-fit md:w-[600px] rounded-[24px] p-5 shadow-ambassadorCard">
        <div className="flex justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <img
                src={LearnLocalLogo}
                className="w-28"
                alt="Team Member"
              />
            </div>
            <div>
              <p className="text-sm text-labelTextColor">
                {getDateTime(ambassadorState.bookedSessions?.startTime)}
              </p>
              <h1 className="text-2xl text-blackie font-medium">
                Live Welcome Session
              </h1>
              <p className="flex gap-1 items-center text-sm text-labelTextColor">
                <DateICon /> {formattedTime}
              </p>
              <p className="text-ambassadorBlue text-sm">
                {ambassadorState.bookedSessions?.teamMember?.firstName || ''}{" "}
                {ambassadorState.bookedSessions?.teamMember?.lastName || ''}
              </p>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleJoinClick}
                  className="w-[74px] h-[37px] bg-ambassadorBlue text-white text-center rounded-full hover:bg-blue-700 transition-colors"
                >
                  Join
                </button>
                <button
                  className="w-[150px] h-[37px] bg-fade text-blackie text-center rounded-full hover:bg-gray-200 transition-colors"
                  onClick={() => goToAmbassadorAdminExperienceDetailView(lastAdminEvent?.id)}
                
                >
                 Event Detail
                </button>
              </div>
            </div>
          </div>
          <div>
            <span onClick={toggleDrawer} className="mt-4 cursor-pointer">
              <InforCircle className="w-7 h-7" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const handleCompleteProfileClick = () => {
    // Check if step one (user profile) is complete
    const level = determineUserLevel();
    
    // If step one is complete (level 1 or 3), navigate to welcome page
    // Otherwise, navigate to profile page to complete step one
    if (level >= 1) {
      navigate(siteRoutes.ambassadorWelcome);
    } else {
      navigate(siteRoutes.ambassadorProfile);
    }
  };

  const renderProfileIncompleteUI = () => (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-full sm:w-[440px] text-center">
        <div className="flex flex-col items-center justify-center gap-2">
          <CallenderSvg className="w-16 h-16" />
          <h2 className="text-ambassadorDarkBlue text-2xl font-medium">
            Please Complete Your Profile
          </h2>
          <p className="text-center text-blackie">
            Please complete your profile to unlock further features.
          </p>
          <button
            onClick={handleCompleteProfileClick}
            className="px-6 py-2 bg-fade text-ambassadorDarkBlue rounded-full mt-2 hover:bg-gray-200 transition-colors"
          >
            Complete Profile
          </button>
        </div>
      </div>
    </div>
  );

  const renderDefaultUI = () => (
    <div className="min-h-[50vh] flex flex-col gap-4">
      <div className="flex flex-col gap-2 w-[351px] bg-white shadow-card p-4 rounded-xl">
        <h1 className="flex justify-between items-center text-blackie text-lg font-medium">
          Mission Statement
          <span
            className="cursor-pointer"
            onClick={() => navigate(siteRoutes.ambassadorEditProfile)}
          >
            <EditIcon />
          </span>
        </h1>
        <p className="text-sm text-blackie">{missionStatement || 'No mission statement yet.'}</p>
      </div>
 
    </div>
  );

  return (
    <div>
      <div
        style={{ backgroundImage: `url(${ambassadorBg})` }}
        className="bg-cover bg-no-repeat w-full h-full md:h-[300px]"
      >
        <div className="container h-full">
          <div className="flex flex-col md:flex-row justify-between items-center relative">
            {/* Text Section */}
            <p className="text-2xl sm:text-3xl text-blackie mt-10 md:mt-24 text-center md:text-left">
              Hello <span className="font-semibold">{userFullName}!</span>
            </p>

            {/* Image Section */}
            <div className="relative z-10 md:absolute md:right-0 md:top-11 mt-10 md:mt-0 flex justify-center md:justify-end w-full md:w-auto">
              <img
                className="w-[300px] sm:w-[400px] md:w-[600px] h-auto object-contain"
                src={ambassadorMainImg}
                alt="ambassadorMainImg"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container md:mt-[-105px]">
        <div className="flex flex-wrap  items-center gap-4">
          <div className="">
            <img
              className="w-36 h-36 object-cover border-4 border-white rounded-full"
              src={userProfilePic || editProfileUser}
              alt="profilePic"
            />
          </div>
          <div className="flex items-center gap-2 bg-ambassadorBlueBg rounded-lg lg:rounded-full px-4 py-2 mt-6 text-sm">
            <span className="text-blackie flex gap-1 items-center">
              <Award />

              {ambassadorTier === "tierOne" && <span>Community Builder</span>}
              {ambassadorTier === "tierTwo" && <span>Community Advocate</span>}
              {ambassadorTier === "tierThree" && <span>Community Leader</span>}
            </span>
            <span className="text-blackie flex gap-1 capitalize items-center">
              <Medal />
              {timeSince}
            </span>
          </div>
        </div>
      </div>

      <div className="container min-h-[50vh]">
        {currentUIState === 'family-profile' && <AmbassadorFamilyprofile />}
        {currentUIState === 'session-ui' && renderSessionUI()}
        {currentUIState === 'event-passed-waiting' && renderEventPassedWaitingUI()}
        {currentUIState === 'waiting-for-approval' && renderWaitingForApprovalUI()}
        {currentUIState === 'not-attended' && renderNotAttendedUI()}
        {currentUIState === 'rejected' && renderRejectedUI()}
        {currentUIState === 'profile-incomplete' && renderProfileIncompleteUI()}
        {currentUIState === 'default' && renderDefaultUI()}
      </div>
      {open && <DrawerAmbassadorRole isOpen={open} onClose={toggleDrawer} />}
      {profileModalOpen && <ProfileCompletionModal />}
    </div>
  );
};

export default AmbassadorHome;
